/* eslint-disable import/prefer-default-export -- singleton composable store */
import type { GirderModelType } from '@girder/components/src';
import type { GirderConfig } from 'platform/web-girder/constants';
import { ref } from 'vue';
import {
  getDataset, getDatasetMedia, getFolder, mergeDatasetConfig, resolveDatasetFolderId,
} from 'platform/web-girder/api';
import { parentDatasetId } from 'dive-common/compositeDatasetId';
import { MultiType } from 'dive-common/constants';

import { useLocation } from './useLocation';

const meta = ref<GirderConfig | null>(null);

export function useDataset() {
  function getMeta(): GirderConfig | null {
    return meta.value;
  }

  function setMeta(dataset: GirderConfig | null): void {
    meta.value = dataset;
  }

  async function loadDataset(datasetId: string): Promise<GirderConfig> {
    const { folderId, compositeId } = await resolveDatasetFolderId(datasetId);
    const [folder, metaStatic, media] = await Promise.all([
      getFolder(folderId),
      getDataset(datasetId),
      getDatasetMedia(datasetId),
    ]);
    const dsMeta = mergeDatasetConfig(metaStatic.data, media.data, compositeId);
    // Only update the shared store for the parent dataset. Per-camera composite
    // loads (parentId/cameraName) must not overwrite multicam metadata used by
    // ViewerLoader pipeline filters and other chrome.
    if (!compositeId) {
      setMeta(dsMeta);
    } else if (!meta.value && metaStatic.data.type === MultiType) {
      // Landing on a camera URL first: prime parent meta so pipeline filters see subType.
      setMeta({
        ...metaStatic.data,
        imageData: [],
        videoUrl: undefined,
      });
    }
    let browseParentId = folder.data.parentId;
    let browseParentCollection = folder.data.parentCollection;
    if (metaStatic.data.type === MultiType || compositeId) {
      const multiCamRootId = parentDatasetId(datasetId);
      const multiCamRootFolder = multiCamRootId === folderId
        ? folder
        : (await getFolder(multiCamRootId));
      browseParentId = multiCamRootFolder.data.parentId;
      browseParentCollection = multiCamRootFolder.data.parentCollection;
    }
    if (browseParentId && browseParentCollection) {
      await useLocation().hydrate({
        _id: browseParentId,
        _modelType: browseParentCollection as GirderModelType,
      });
    } else {
      throw new Error(`dataset ${datasetId} was not a valid girder folder`);
    }
    return dsMeta;
  }

  return {
    meta,
    getMeta,
    setMeta,
    loadDataset,
  };
}
